import { collectCombatantSources } from './CombatantNormalizer';
import {
  validateCreatureInventory,
  validateCreatureMechanics,
  creatureActionPrerequisite
} from '../../../../shared/rules/creatureAbilities';

/** Select embedded definitions only. No library lookup or runtime repair.
 * rule_reference: AGENTS.md, canonical snapshots; date_of_change: 2026-09-05.
 */
export const getCreatureMechanicsData = subject => {
  const sources = collectCombatantSources(subject);
  const versioned = sources.find(source => Object.prototype.hasOwnProperty.call(source, 'contentVersion'));
  if (versioned) return versioned;
  // Missing versions are blockers for monster snapshots, not a reason to run
  // the name/prose-based catalog. This does not modify data or infer mechanics.
  // date_of_change: 2026-09-05
  if (subject?.type === 'monster' || subject?.monsterData) {
    return sources.find(source => Array.isArray(source.actions) && Array.isArray(source.traits)) || subject;
  }
  return null;
};

export const creatureMechanicsProjection = subject => {
  const data = getCreatureMechanicsData(subject);
  if (!data) return {};
  // Senses are part of the canonical creature mechanics projection because
  // scoped check traits must expose their authored passive Perception without
  // a runtime fallback. date_of_change: 2026-09-06.
  return Object.fromEntries(['contentVersion', 'traits', 'actions', 'bonusActions', 'reactions', 'senses', 'resistanceBlock', 'conditionImmunities', 'savingThrows', 'equipmentState']
    .map(key => [key, data[key]]));
};

export const assertCreatureCombatReadiness = (actor, action) => {
  const data = getCreatureMechanicsData(actor);
  if (data) validateCreatureInventory(data, actor.id);
  if (action?.mechanics) validateCreatureMechanics(action, actor.id, 'actionData');
};

export const buildCreatureAbilityCatalog = subject => {
  const data = getCreatureMechanicsData(subject);
  if (!data) return null;
  let readinessError = null;
  try { validateCreatureInventory(data, subject.id || data.id); }
  catch (error) { readinessError = error.message; }
  const actor = { ...data, ...subject };
  // Preserve every action, including malformed entries, so a GM sees the
  // offending source instead of a silently shortened action list.
  return ['actions', 'bonusActions', 'reactions'].flatMap(bucket => {
    const entries = Array.isArray(data[bucket]) ? data[bucket] : [{ id: `${bucket}:invalid`, name: `${bucket} unavailable`, text: readinessError }];
    return entries.map(value => {
      const entry = value && typeof value === 'object' ? value : { name: 'Invalid ability', text: String(value) };
      return ({
      ...entry,
      description: entry.text,
      type: entry.mechanics?.kind === 'attack' || entry.mechanics?.kind === 'multiattack' ? 'attack' : 'ability',
      actionType: entry.activationType,
      isMonsterAction: true,
      originalAction: entry,
      ...(entry.attack ? {
        attackType: entry.attack.type,
        toHit: entry.attack.toHit,
        toHitBonus: entry.attack.toHit,
        requiresAttackRoll: true,
        range: entry.attack.range || { normal: entry.attack.reach, long: entry.attack.reach },
        reach: entry.attack.reach,
        damage: entry.attack.damage,
        damageFormula: entry.attack.damage?.[0]?.dice,
        damageType: entry.attack.damage?.[0]?.type
      } : {}),
      readinessError,
      unavailableReason: readinessError || creatureActionPrerequisite(actor, entry)
      });
    });
  });
};
